/**
 * Global command palette (⌘K / Ctrl+K). Fuzzy-ish filter over nav destinations and
 * quick actions, keyboard-navigable. Built from scratch — no command-menu library.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search } from 'lucide-react';
import { ALL_NAV_ITEMS } from '../../config/nav.js';
import { useTheme } from '../../theme/ThemeProvider.jsx';
import styles from './CommandPalette.module.css';

export function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { toggle } = useTheme();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const commands = useMemo(() => {
    const nav = ALL_NAV_ITEMS.filter((i) => !i.phase || i.phase <= 7).map((i) => ({
      id: `nav:${i.to}`,
      label: i.label,
      hint: 'Navigate',
      icon: i.icon,
      run: () => navigate(i.to),
    }));
    const actions = [
      {
        id: 'action:theme',
        label: 'Toggle light / dark theme',
        hint: 'Action',
        run: () => toggle(),
      },
    ];
    return [...nav, ...actions];
  }, [navigate, toggle]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => c.label.toLowerCase().includes(q));
  }, [commands, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => setActive(0), [query]);

  if (!open) return null;

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[active];
      if (cmd) {
        cmd.run();
        onClose();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className={styles.searchRow}>
          <Search size={18} className={styles.searchIcon} />
          <input
            ref={inputRef}
            className={styles.input}
            placeholder="Search pages and actions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd className={styles.esc}>Esc</kbd>
        </div>

        <ul className={styles.results}>
          {filtered.length === 0 && <li className={styles.empty}>No matches</li>}
          {filtered.map((cmd, i) => {
            const Icon = cmd.icon;
            return (
              <li key={cmd.id}>
                <button
                  className={`${styles.item} ${i === active ? styles.itemActive : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => {
                    cmd.run();
                    onClose();
                  }}
                >
                  {Icon ? <Icon size={17} /> : <span className={styles.dot} />}
                  <span className={styles.itemLabel}>{cmd.label}</span>
                  <span className={styles.itemHint}>{cmd.hint}</span>
                  {i === active && <CornerDownLeft size={14} className={styles.enter} />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default CommandPalette;
